import { useUserStore } from '@/app/stores/useUserStore';
import { captureRequest } from '@/lib/analytics/posthog-events';
import { isInAppWebView } from '@/lib/utils';
import i18next from 'i18next';
import { toast } from '@/hooks/use-toast';
import { goToLogin } from './login';
// API配置和HTTP客户端
// 动态获取API基础路径，部署时自动使用当前域名+/api
export const getApiBaseUrl = () => {
  // 服务端环境：使用环境变量或默认值api
  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8181/sqx_fast';
};

const API_BASE_URL = getApiBaseUrl();
const WS_URL = process.env.NEXT_PUBLIC_WS_URL;

// 通用响应类型
export interface ApiResponse<T = any> {
  code: number;
  msg: string;
  enMsg?: string;
  data?: T;
}

/** 个人信息概览 - 用户总价值 */
export interface UserProfileOverviewTotalValue {
  currentUserTotalConsumeU?: number;
}

/** 个人信息概览 - 会员等级 */
export interface UserProfileOverviewMembership {
  consumeLevel?: number;
  lighted?: boolean;
  currentUserTotalExp?: number;
  currLevelTotalExp?: number;
  currLevelMinExp?: number;
  currLevelMaxExp?: number;
  upNeedExp?: number;
  explode?: boolean;
}

/** 个人信息概览 - 邀请排名 */
export interface UserProfileOverviewInvitationRank {
  rank?: number;
  totalRewardUsd?: number;
  commissionAmountUsd?: number;
}

/** 个人信息概览 - 积分排名 */
export interface UserProfileOverviewPointsRank {
  rank?: number;
  totalPoints?: number;
}

/** 个人信息概览 data */
export interface UserProfileOverviewData {
  userTotalValue?: UserProfileOverviewTotalValue;
  membershipLevel?: UserProfileOverviewMembership;
  invitationRank?: UserProfileOverviewInvitationRank;
  pointsRank?: UserProfileOverviewPointsRank;
}

// 用户信息类型
export interface UserInfo {
  userId: number;
  userName: string;
  avatar: string;
  phone: string;
  isRoom: number;
  roomId?: number;
  roomNo?: string;
  banner?: string;
}

export interface UserProfile {
  userId?: number;
  userLabels?: string;
  trtcUserId?: string;
  name: string;
  /** 用户唯一标识 Handle（后端字段 username 语义已切换为 handle） */
  username: string;
  handle?: string;
  /** handle 是否已修改：0 未修改（可编辑），1 已修改（锁定） */
  handleModified?: 0 | 1;
  avatar: string;
  banner: string;
  bio: string;
  twitter?: string;
  badge?: string;
  followers: string;
  following: string;
  userPosts: number;
  joinDate: string;
  location?: string;
  website?: string;
  shareCount?: number;
  followerStatus?: FollowStatus;
  followStatus?: FollowStatus;
  isStreamer?: 0 | 1;
  invitationCode: string;
  walletAddress: string;
  walletNetwork: string;
  solWalletAddress?: string;
  evmWalletAddress?: string;
  isRoom?: number;
  roomId?: number;
  remainingInviteCount?: number;
  inviter?: {
    inviterCode: string;
    userId: number;
    username: string;
    avatar: string;
    /** 绑定上级时间（若接口返回） */
    bindTime?: string;
  };
  bindStatus?: {
    inviteCode: string;
    bindReason: string;
    applicationStatus: number; /** -1 未申请；0 待审核；1 通过；2 拒绝 */
    applicationRejectReason?: string;
  };
  totalBalance?: number;
  hlBalance?: number;
  userSig?: string;
  pointsUserList: any[];
  consumeLevel?: number;
  lighted?: boolean;
  isBlocked?: boolean; // 非自己的用户，是否被拉黑
  banPost?: boolean; // 是否被禁言
  banComment?: boolean; // 是否被禁言
  banGroupChat?: boolean; // 是否被禁言
  banLiveChat?: boolean; // 是否被禁言
  isOfficial?: number; // 是否为官方认证 0否 1是
  isWalletBlacklisted?: boolean; // 是否在黑名单/风险用户
  applyInvite?: {
    applicationStatus: number; /** -1 未申请；0 待审核；1 通过；2 拒绝 */
  };
}

/** 主播福袋记录列表项（GET /app/luckybag/anchor/records） */
export interface AnchorLuckyBagRecordItem {
  luckyBagId: number;
  createTime: string;
  chainId: number;
  tokenSymbol: string;
  totalAmount: number;
  count: number;
  participantCount: number;
  winnerCount: number;
  /** 实际领取人数（≤ winnerCount），新流程下中奖与领取解耦 */
  claimedCount?: number;
  keyword: string;
  distributeType: number;
  /** 0=待支付 1=进行中 2=开奖中 3=已开奖 4=已失败 5=链上确认中 */
  status: number;
  countdownMinutes: number;
  preconditions: LuckyBagCreatePreconditions | string;
  groupId?: string;
  roomName?: string;
  roomId?: number;
  /** 群组名称 */
  groupName?: string;
}

export interface AnchorLuckyBagRecordsPage {
  list: AnchorLuckyBagRecordItem[];
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
}

export interface AnchorLuckyBagWinnerUser {
  userId: number;
  userName: string;
  avatar: string;
  userLabels?: string;
  userLevel?: number;
}

export interface AnchorLuckyBagWinner {
  winnerId: number;
  user: AnchorLuckyBagWinnerUser;
  winAmount: number;
  txHash: string | null;
  /** 0=待发款 1=已到账 2=发款失败 3=已退回 */
  payoutStatus: number;
  /** 领取状态：0=待领取 1=已领取 2=未领取已过期 */
  claimStatus?: number;
  winTime: string;
}

/** 主播福袋详情（GET /app/luckybag/anchor/record-detail） */
export interface AnchorLuckyBagDetail extends AnchorLuckyBagRecordItem {
  winners: AnchorLuckyBagWinner[];
  refund: {
    amount: number;
    txHash: string | null;
    status: number | null;
  };
}

/** TRTC 公开群（GET /app/chat/list），字段名以后端为准 */
export interface AppChatListItem {
  groupId?: string;
  id?: string;
  groupName?: string;
  name?: string;
  title?: string;
}

/** POST /app/luckybag/create */
export interface LuckyBagCreatePreconditions {
  followHost?: boolean;
  joinGroupId?: string;
  joinGroupName?: string;
  requireCode?: boolean;
  keyword?: string;
}

/** GET /app/luckybag/precondition 观众侧门槛状态（文档 v2） */
export type LuckyBagViewerConditionType = 'followHost' | 'joinGroup' | 'participationCode';

export interface LuckyBagViewerPreconditionItem {
  type: LuckyBagViewerConditionType;
  required: boolean;
  fulfilled: boolean;
  groupId: string | null;
  groupName: string | null;
  code: string | null;
}

export interface LuckyBagViewerPreconditionData {
  conditions: LuckyBagViewerPreconditionItem[];
  /** 全部条件满足，已入抽奖池 */
  joined: boolean;
}

/** 观众福袋记录 — 主播信息（GET /app/luckybag/viewer/records） */
export interface LuckyBagViewerRecordAnchor {
  userId: number;
  userName: string;
  avatar: string;
  userLabels?: string | null;
  userLevel?: number | null;
  handle?: string | null;
}

/** 观众福袋记录列表项 */
export interface LuckyBagViewerRecordItem {
  luckyBagId: number;
  createTime: string;
  anchor: LuckyBagViewerRecordAnchor;
  tokenSymbol: string;
  totalAmount: number;
  keyword: string;
  /** 与主播端福袋 status 一致 */
  status: number;
  /** 中奖金额，null = 未中奖 */
  winAmount: number | null;
  txHash: string | null;
  /** 0=待发款 1=已到账 2=失败 3=已退回 */
  payoutStatus: number;
  /** 领取状态：0=待领取 1=已领取 2=未领取已过期 */
  claimStatus?: number;
  /** 发款链（可选，用于区块浏览器） */
  chainId?: number;
  room: LiveRoom;
}

export interface LuckyBagViewerRecordsPage {
  list: LuckyBagViewerRecordItem[];
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
}

export interface LuckyBagCreateBody {
  /** 可选；不传时由服务端根据当前开播/会话解析 */
  roomId?: number;
  chainId: number;
  tokenAddress: string;
  tokenSymbol: string;
  decimals: number;
  totalAmount: number;
  count: number;
  /** 1=平均分配 2=随机金额 */
  distributeType: number;
  /** 枚举 1 / 3 / 5 / 10 */
  countdownMinutes: number;
  keyword?: string;
  preconditions: LuckyBagCreatePreconditions;
}

export interface LuckyBagCreateResult {
  luckyBagId: number;
  platformEoaAddress: string;
  estimatedGas?: number | null;
}

export interface LuckyBagRequirements {
  needFollowHost?: boolean;
  needJoinGroup?: boolean;
  participationCode?: string;
}

export interface LuckyBagInfo {
  luckyBagId: number;
  totalAmount: number;
  tokenSymbol: string;
  chainId: number;
  count: number;
  distributeType: number;
  deadlineTimestamp: number;
  status: number;
  participantCount: number;
  preconditions: LuckyBagCreatePreconditions;
  joined: boolean;
  bagStatus?: string;
  /** 领取窗口截止时间戳(ms)，仅 claiming 阶段有值（F6.6 对齐抖音） */
  claimDeadline?: number;
  myWinnerInfo?: AnchorLuckyBagWinner;
}

export interface LuckyBagDrawResult {
  type?: number;
  luckyBagId: number;
  won: boolean;
  amount: string;
  tokenSymbol: string;
  chainId: number;
  /** true=新流程，需点击领取；缺省/false=旧流程，自动到账 */
  claimable?: boolean;
  /** 领取窗口截止时间戳(ms) */
  claimDeadline?: number | null;
}

/** POST /app/luckybag/claim 领取响应 */
export interface LuckyBagClaimResult {
  claimed: boolean;
  winAmount: number;
  tokenSymbol: string;
  chainId: number;
}

/** 福袋领取相关错误码（新增 F6.6） */
export const LUCKY_BAG_CLAIM_ERROR = {
  BAG_NOT_FOUND: 1110,
  NOT_CLAIMING: 1111,
  NOT_IN_ROOM: 1150,
  WINDOW_EXPIRED: 1151,
  NOT_WINNER: 1152,
  ALREADY_CLAIMED: 1153,
} as const;
// 直播间信息类型
export interface LiveRoom {
  roomId: number;
  roomNo: string;
  roomName: string;
  userId: number;
  userName: string;
  avatar: string;
  roomUserCount: number; // 正在观看人数
  viewCount?: number; // 累计有效观看人数
  status: number;
  roomType: number;
  roomImg: string;
  createTime: string;
  playingMethod?: string;
  seconds?: number;
  giftIncome?: number;
  totalIncomeUsd?: number; // 累计打赏金额
  playbackUrl?: string;
  recordingVideoUrl?: string;
  flvStreamUrl?: string;
  streamingMode?: 'OBS' | 'RTC';
  obsStreamUrl?: string;
  obsStreamKey?: string;
  obsServer?: string;
  isLive?: boolean;
  userSig?: string;
  sdkAppId?: number;
  totalViewerCount?: number;
  maxUserCount?: number;
  userLabels?: string;
  scheduleId?: number;
  activeLuckyBag?: boolean;
  announcement?: string | null;
  handle?: string;
}

/** POST /app/room/schedule/create 请求体 */
export interface CreateLiveSchedulePayload {
  roomName: string;
  roomType: number;
  roomImg: string;
  scheduleTime: string;
  playingMethod?: string;
  announcement?: string;
  description?: string;
}

/** POST /app/room/schedule/create 返回 data */
export interface CreateLiveScheduleData {
  scheduleId: number;
  talkId: number | null;
}

/** GET /app/room/schedule/myList 单条预约 */
export interface LiveScheduleListItem {
  id: number;
  userId: number;
  roomName: string;
  roomType: number;
  roomImg: string;
  playingMethod?: string;
  announcement?: string;
  description?: string;
  scheduleTime: string;
  /** 预约状态，具体含义以后端为准：常见 1=待开播 2=直播中 3=已结束 4=已取消 */
  status: number;
  talkId: number | null;
  roomId: number | null;
  createTime: string;
  updateTime: string;
  userName?: string;
  avatar?: string;
  reserveCount?: number;
}

/** GET /app/room/schedule/myList data */
export interface LiveScheduleListData {
  list: LiveScheduleListItem[];
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
}

/** GET /app/room/schedule/myAppointmentList 单项 */
export interface MyAppointmentListItem {
  scheduleId: number;
  title: string;
  cover: string;
  /** 展示用：0=未开始，1=进行中，2=已结束 */
  status: number;
  statusDesc: string;
  startTime: string;
  reserveCount: number;
  /** playing_method 文案 */
  tag?: string;
  roomType: number;
  streamerInfo?: UserFollow;
}

/** GET /app/room/schedule/myAppointmentList data */
export interface MyAppointmentListData {
  list: MyAppointmentListItem[];
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
}

/** GET /app/room/schedule/hotAppointmentList 单项 */
export interface HotAppointmentListItem {
  scheduleId: number;
  title: string;
  cover: string;
  /** 展示用：0=未开始，1=进行中，2=已结束 */
  status: number;
  statusDesc: string;
  startTime: string;
  reserveCount: number;
  /** playing_method 文案 */
  tag?: string;
  roomType: number;
  streamerInfo?: UserFollow;
}

/** GET /app/room/schedule/hotAppointmentList data */
export interface HotAppointmentListData {
  list: HotAppointmentListItem[];
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
}

/** GET /app/room/schedule/detail — roomInfo（已开播/已结束时可能有值） */
export interface LiveScheduleRoomInfo {
  roomId: number;
  roomName: string;
  roomImg: string;
  /** 直播间状态：-1 待推流、1 直播中、0 已结束 */
  status: number;
  roomUserCount: number;
  streamingMode: string;
  recordingVideoUrl: string | null;
}

/** GET /app/room/schedule/detail data */
export interface LiveScheduleDetail {
  scheduleId: number;
  userId: number;
  userName: string;
  avatar: string;
  roomName: string;
  roomType: number;
  roomImg: string;
  playingMethod?: string;
  announcement?: string;
  description?: string;
  scheduleTime: string;
  status: number;
  statusDesc?: string;
  talkId: number | null;
  roomId: number | null;
  roomInfo: LiveScheduleRoomInfo | null;
  createTime: string;
  relationshipStatus: FollowStatus;
  streamerInfo: StreamerInfo | null;
  reserveCount?: number;
}

/** POST /app/room/schedule/update 请求体（与创建字段一致，另带 scheduleId） */
export type UpdateLiveSchedulePayload = CreateLiveSchedulePayload & { scheduleId: number };

// 历史观看记录
export interface HistoryWatchLiveRoom {
  room_id: number;
  watch_time_formatted: string;
  streamer_current_avatar: string;
  created_at: string;
  streamer_create_time_formatted: string;
  room_img: string;
  streamer_user_name: string;
  streamer_create_time: string;
  room_name: string;
  fans_count: number;
  relationship_status: 0 | 1 | 2;
  updated_at: string;
  watch_time: string;
  follow_count: number;
  streamer_id: number;
  streamer_handle?: string;
  id: number;
  streamer_name: string;
  streamer_sex: number;
  streamer_avatar: string;
  streamer_is_room: number;
  streamer_room_id: number;
}

// Banner信息类型
export interface Banner {
  bannerId: number;
  title: string;
  imgUrl: string;
  jumpUrl: string;
  classify: number;
  sort: number;
  status: number;
  createTime: string;
}

// 公告
export interface Announcement {
  id: number;
  publisherId?: number;
  status: number;
  title: string;
  titleEn?: string | null;
  titleJa?: string | null;
  titleKo?: string | null;
  titleYue?: string | null;
  content: string;
  contentEn?: string | null;
  contentJa?: string | null;
  contentKo?: string | null;
  contentYue?: string | null;
  startTime: string;
  endTime?: string;
  createTime?: string;
  updateTime?: string;
  sortOrder?: number;
}

// 公告列表分页响应
export interface AnnouncementListResponse {
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
  list: Announcement[];
}
// 分类信息类型
export interface Category {
  id: number;
  createTime: string;
  name: string;
  nameEn?: string;
  nameJa?: string;
  nameKo?: string;
  nameYue?: string;
  imageUrl: string;
  state: number;
  classify: number;
  url: string;
  sort: number;
  describes: string;
  languageType: string;
  languageName: string;
}

// 内容分类类型
export interface ContentCategory {
  id: number;
  name: string;
  nameEn: string;
  nameKo?: string | null;
  nameJa?: string | null;
  nameYue?: string | null;
  sort: number;
}
export interface CategoryBase {
  classify: number;
  languageType: string;
  sort: number;
  languageName: string;
  url: string;
  describes: string;
  describesEn?: string;
  createTime: string;
  imageUrl: string;
  name: string;
  nameEn?: string;
  nameJa?: string;
  nameKo?: string;
  nameYue?: string;
  id: number;
  state: number;
}
export interface CategoryRoom extends CategoryBase {
  roomCount: number;
  roomList: LiveRoom[];
}

export interface CategoryRoomNew {
  roomCount: number;
  category: CategoryBase;
  roomList: LiveRoom[];
}

export type FollowStatus = 0 | 1 | 2;
// 用户关注信息类型
export interface UserFollow {
  handle?: string;
  userId: number | string;
  userName: string;
  avatar: string;
  status: number; // 1: 互相关注, 2: 单方面关注
  followStatus: FollowStatus; //（0未关注，1已关注，2互相关注）
  followCount: number;
  totalViewCount: number;
  compareStatus: number; // 直播环比状态 0 下降 1上升 2稳定
  updateTime: string;
  sex?: number;
  age?: number;
  onLine?: number;
  roomId?: number;
  loginTime?: string;
  createTime: string;
  isStreamer?: 0 | 1;
  isOfficial?: number;
  userLabels?: string;
}

export interface UserFollowRank {
  userId: number;
  userName: string;
  avatar: string;
  status: number; // 1: 互相关注, 2: 单方面关注
  followStatus: number; //（0未关注，1已关注，2互相关注）
  followCount: number;
  totalViewCount: number;
  updateTime: string;
  sex?: number;
  age?: number;
  onLine?: number;
  roomId?: number;
  loginTime?: number;
  createTime: string;
}

export interface CryptoData {
  coinSymbol?: string;
  coinPrice: number;
  coinPriceFormatted: string;
  chainId: number;
  address: string;
  icon?: string;
  coinIcon?: string;
  tokenSymbol?: string;
}

export interface HashData {
  tagId?: number;
  tagName: string;
  mentionCount?: number;
  createTime?: string;
}

// 首页汇聚内容项类型
export interface FeaturedContentItem {
  contentType: 'video' | 'talk' | 'schedule';
  id: number;
  title: string;
  coverImage: string;
  coverDimension?: number[];
  author: {
    userId: number;
    userName: string;
    avatar: string;
    userLabels?: string;
    handle?: number;
  };
  timestamp: string;
  extraData: FeaturedVideoExtraData | FeaturedTalkExtraData;
}

// 视频类型的扩展数据
export interface FeaturedVideoExtraData {
  roomId: number;
  roomUserCount: number;
  status: number; // 0-关播，1-正在直播
  viewCount: number;
  scheduleId: number;
  scheduleTime: string;
  scheduleStatus: number;
  activeLuckyBag?: boolean;
}

// 动态类型的扩展数据
export interface FeaturedTalkExtraData {
  content: string;
  images: string[];
  videoUrl?: string;
  tags: string[];
  comments: number;
  likes: number;
  shares: number;
  shareCount?: number;
  isShared?: boolean;
  isLiked: boolean;
  isFavorite: number; // 0-否，1-是
  isFollowing: number; // 0-否，1-是
  likeCount?: number;
  viewCount?: number;
  polymarketEventSlug?: string;
  roomId?: number;
}

// 动态/帖子信息类型
export interface Post {
  imageUrl?: string;
  coverImage?: string;
  coverDimension?: number[];
  videoUrl?: string;
  author: {
    name: string;
    avatar: string;
    userId: number;
    isOfficial?: number;
    userLabels?: string;
    handle?: string;
  };
  commentsList: Comment[];
  tags: string[];
  timestamp: string;
  content: string;
  id?: number;
  talkId: number;
  cryptoData: CryptoData;
  commentCount: number;
  likeCount: number;
  shareCount: number;
  isLiked: boolean;
  isCollected: boolean;
  isShared: boolean; // 是否已分享过（已分享则不再统计计数）
  viewCount: number; // 浏览数
  isTop?: number; // 是否置顶：0-未置顶，1-已置顶
  topTime?: string; // 置顶时间
  polymarketEventSlug?: string; // 关联的预测市场事件 slug
  contentCategoryIds?: number[]; // 内容分类ID列表
  // 转发帖子相关字段
  originalTalkId?: number; // 原帖ID（转发时存在）
  quotedTalkDeleted?: boolean; // 引用的原帖是否已被删除
  otherUserList?: { id: number; name: string; avatar: string }[]; // 其他转发用户列表
  // 直播间帖子才有的字段
  roomId?: number; // 直播间ID
  roomDetailInfo?: LiveRoom;
  scheduleDetail?: LiveScheduleDetail;
  videoCover?: string; // 视频封面
  status: number; // 1. 待审核 2.已发布 3.已下架
  // 内容审核相关字段（见后端内容审核接口文档）
  displayStatus?: 'PENDING' | 'PUBLISHED' | 'OFF_SHELF_EDITABLE' | 'OFF_SHELF_APPEALABLE' | 'REVIEWING' | 'REVIEW_REJECTED';
  rejectReasonType?: 'PORN' | 'POLITICS' | 'ADS' | 'TERRORISM' | 'ABUSE' | 'DUPLICATE' | 'MISINFORMATION' | 'HARASSMENT' | 'INFRINGEMENT' | 'OTHER';
  takedownSource?: 'AUTO' | 'MANUAL';
  takedownTime?: number; // 下架时间戳，用于倒计时
  errorImgs?: number[]; // 违规媒体索引
}

// 评论信息类型
export interface Comment {
  commentId: number;
  nickName: string;
  avatar: string;
  content: string;
  createTime: string;
  userId: number;
  talkId: number;
  parentId?: number;
  rootCommentId?: number;
  byUserId?: number;
  byNickName?: string;
  byAvatar?: string;
  /** 评论点赞数（列表接口返回） */
  likeCount?: number;
  /** 当前用户是否已点赞（若列表接口返回） */
  isLiked?: boolean;
  handle?: string;
  targetComment?: {
    commentId: number;
    nickName: string;
    userId: number;
    avatar: string;
    handle: string;
  };
  subComments?: Comment[];
}

// 直播间聊天信息类型
export interface RoomChat {
  roomChatId: number;
  roomId: number;
  content: string;
  messageType: number; // 1文字 2图片 3语音 4表情包 5礼物 6订单/跟单 7 直播间关闭 8 是主播下单 9 hl下单
  userId: number;
  userName: string;
  avatar: string;
  createTime: string;
  voiceLength?: string;
  chatImg?: string;
}

// 礼物信息类型
export interface Gift {
  id: number;
  giftId: number;
  giftName: string;
  giftNameEn?: string;
  giftNameJa?: string;
  giftNameKo?: string;
  giftNameYue?: string;
  giftImg: string;
  giftGif: string;
  giftVideo: string;
  giftIcon: string;
  giftMoney: number;
  status: number;
  createTime: string;
  quantity: number;
  valueUSD: number;
  senderAvatar?: string;
  senderName?: string;
  txn: string;
  timestamp: string;
  userAvatar?: string;
  userName?: string;
  userId?: string;
  isHigh?: boolean; // 是否为高价值礼物
  giftQuantity?: number | string;
  chainId?: number | string;
  chainName?: string;
  streamerAddress?: string;
  orderNo?: string;
  txHash?: string;
  fullScreenDisplay?: 0 | 1; // 0: 不显示全屏动画, 1: 显示全屏动画
}

// 礼物使用记录类型
export interface GiftUser {
  giftUserId: number;
  giftId: number;
  giftName: string;
  giftNameEn?: string;
  giftNameJa?: string;
  giftNameKo?: string;
  giftNameYue?: string;
  giftImg: string;
  giftMoney: number;
  userId: number;
  sendUserId: number;
  roomId: number;
  createTime: string;
  userMoney: number;
}

// 礼物赠送的金额
export interface GiftAmount {
  giftId: number;
  quantity: number;
  requiredAmount: number;
  giftName: string;
  giftNameEn?: string;
  giftNameJa?: string;
  giftNameKo?: string;
  giftNameYue?: string;
  giftPrice: number;
  totalUsdValue: number;
  tokenPrice: number;
  currency: string;
  contractAddress: string;
  platformAddress: string;
  platformFeePercentage: number;
  streamerAddress: string;
}

// 统一消息类型
export interface UnifiedMessage {
  id: number;
  messageType: number;
  category: 'announcement' | 'system' | 'account' | 'interactions' | 'transaction'; // TODO: 不需要保留旧通知时、transaction删掉
  template: string;
  variables: Record<string, any>;
  createTime: string;
  isSee: number; // 0未读，1已读
  aggregateCount?: number; // 聚合数
}

// 提醒信息类型
export interface Notification {
  id: number;
  category: 'announcement' | 'system' | 'account' | 'interactions';
  // 1(回复)、2(点赞)、3(关注)、4(分享)、5(打赏)、6(下单)、7(跟单)、8(反馈留言)、9合约交易成功、10合约交易失败、14正在直播提醒、15公告、16现货交易成功、17现货交易失败、18充值成功、19预约直播提醒、20主播关闭预约通知、25内容审核通知;
  messageType: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 25;
  title: string;
  content: string;
  createAt: string; // TODO: 不需要保留旧通知时、删除
  createTime: string;
  userName: string;
  byUserName: string;
  isSee: number; // 0未读，1已读
  detailData: {
    type: 'reply' | 'like' | 'follow' | 'share' | 'reward' | 'order' | 'followOrder' | 'feedback' | 'like_comment';
    actionText?: string;
    byUser?: {
      userId: number;
      userName: string;
      avatar: string;
      handle?: string;
    };
    relatedComment?: {
      commentContent?: string;
      commentId: number;
      content: string;
      createTime: string;
    };
    relatedTalk?: {
      // 对于回复、点赞、分享消息
      talkId: number;
      content: string;
      preview: string;
      createTime: string;
    };
    followerInfo?: {
      // 对于关注消息
      userId: number;
      userName: string;
      avatar: string;
    };
    // 5\6\7格式
    roomChatInfo?: {
      content: string;
      roomId?: number;
      roomName?: string;
    };
    // 8(反馈留言)格式
    feedbackId?: number;
    problemType?: string;
    problemDescription?: string;
    status?: number;
    latestMessageTime?: string;
    latestMessageContent?: string;
  };
  aggregateCount?: number; // 聚合数
  groupMessageIds?: number[]; // 聚合消息的id列表
}

export type CoinPriceCoin = 'SOL' | 'USDT' | 'USDC' | 'BNB';
export interface CoinPrice {
  coin: CoinPriceCoin;
  price: number;
}
export interface CoinPriceToken {
  id: number;
  rewardChainConfigId: number;
  chainId: number;
  tokenContractAddress: string;
  tokenSymbol: string;
  tokenName: string;
  decimals: number;
  price: number;
  icon: string;
  status: number;
  sortOrder: number;
  createTime: string;
  updateTime: string;
  isMain?: boolean;
}
export interface CoinPriceItem {
  id: number;
  chainId: number;
  name: string;
  symbol: string;
  icon: string;
  rpcUrl: string;
  explorerUrl: string;
  contractAddress: string;
  platformAddress: string;
  platformCommissionRate: number;
  status: number;
  sortOrder: number;
  tokens: CoinPriceToken[];
}
export interface CoinPriceList {
  evmWalletAddress: string;
  solWalletAddress: string;
  coinPriceList: CoinPriceItem[];
}

// 礼物使用记录类型
export interface GiftUserRank {
  totalGiftAmount: number;
  userAvatar: string;
  ranking: number;
  userName: string;
  userId: number;
  consumeLevel: number;
  lighted: boolean;
  isOfficial: boolean;
  userLabels?: string;
  handle?: string;
}

// 主播信息
export interface StreamerInfo {
  handle?: string;
  consumeLevel: number;
  lighted: boolean;
  followCount: number;
  createTime: string;
  sex: number;
  postCount: number;
  fansCount: number;
  avatar: string;
  userName: string;
  userId: number;
  bio: string;
  streamerAddress: string;
  streamerEvmWalletAddress: string;
  streamerSolWalletAddress: string;
  twitter?: string;
  userLabels?: string;
  isBooked?: boolean;
  relationshipStatus?: number;
}

// 回放视频信息类型
export interface RecordingVideo {
  duration: number;
  videoIndex: number;
  videoUrl: string;
}

// 成为主播
export interface BecomeStreamer {
  status: number;
  countryOrRegion: string;
  isObs: string;
  describe: string;
  screenshotUrl: string;
  isTrade: string;
  telegramId: string;
  twitterHandle: string;
  email: string;
  solWalletAddress: string;
  evmWalletAddress: string;
}

export interface DexToken {
  tokenId: number;
  symbol: string;
  name: string;
  address: string;
  icon: string;
  status: number;
  chainId: number;
  decimals: number;
  totalSupply: number;
  marketCap: number;
  volume24h: number;
  priceChange24h: number;
  liquidity: number;
  isVerified: number;
  riskLevel: string;
  loading: boolean;
}
export interface MainstreamToken {
  id: number;
  symbol: string;
  name: string;
  icon: string;
  address: string;
  price: number;
  decimals: number;
}
export interface UserShopToken {
  id: number;
  chainId: number;
  dexTokenId: number;
  userId: number;
  status: number;
  createTime: string;
  updateTime: string;
  coinIcon: string;
  tokenSymbol: string;
  tokenName: string;
  coinPrice: number;
  decimals: number;
  tokenContractAddress: string;
  buyCount?: number;
  mainstreamTokens: MainstreamToken[];
}

export interface Order {
  id: number;
  orderId: string;
  txHash: string;
  fromSymbol: string;
  fromTokenAddress: string;
  fromTokenIcon: string;
  fromAmount: string;
  toSymbol: string;
  toTokenAddress: string;
  toTokenIcon: string;
  toAmount: number;
  userName: string;
  userId: string;
  userAvatar: string;
  roomName: string;
  roomUserName: string;
  roomUserId: string;
  status: string;
  createTime: string;
  payTime: string;
}

export interface Feedback {
  id: number;
  problemDescription: string;
  problemPage: string;
  screenshotUrl: string;
  problemType: string;
  createTime: string;
  status: string | number;
  updateTime: string;
}

// 反馈详情
export interface FeedbackDetail extends Feedback {
  userId?: number;
  adminReply?: string;
  bugType?: string;
  priorityLevel?: string;
  tag?: string;
  contactEmail?: string;
  satisfactionScore?: number;
  ratingComment?: string;
}

// 反馈留言
export interface FeedbackMessage {
  id: number;
  feedbackId: number;
  userId?: number;
  adminId?: number;
  messageType: number; // 1用户留言 2客服留言
  content: string;
  createTime: string;
  admin?: {
    userID?: number;
    username?: string;
  };
}

// 反馈详情响应
export interface FeedbackDetailResponse {
  feedback: FeedbackDetail;
  messages: FeedbackMessage[];
}

// 举报工单
export interface ReportItem {
  id: number;
  roomId: number;
  reportType: string;
  problemDescription: string;
  status: number; // 1待处理/2处理中/3已处理
  adminReply?: string;
  createTime: string;
  updateTime: string;
  room: {
    roomId: number;
    roomName: string;
    roomUserName: string;
    roomUserAvatar: string;
    userId: number;
    roomImg: string;
  };
}

// 用户举报工单
export interface UserReportItem {
  id: number;
  userId: number;
  reportedUserId: number;
  reportType: string;
  categoryCode?: number; // 举报分类代码
  reportCategory?: number; // 举报分类代码
  problemDescription: string;
  status: number; // 1待处理/2处理中/3已处理
  createTime: string;
  screenshotUrl?: string; // 截图URL
  user: {
    userId: number;
    userName: string;
    avatar: string;
  };
  reportedUser: {
    userId: number;
    userName: string;
    avatar: string;
  };
}

// 举报类型
export interface ReportType {
  reportType: string;
  typeName: string;
  needDescription: boolean;
}

// 举报分类
export interface ReportCategory {
  code: number;
  name: string;
}

// 积分信息
export interface PointsRecord {
  user_id: number;
  points_change: number;
  process_time: string;
  type_code: string;
}

// 直播间订单信息
export interface OrderInfo {
  orderId: string;
  fromAmount: number;
  toAmount: number;
  toToken: {
    symbol: string;
    address: string;
    chainId: number;
    price: number;
    decimals: number;
    icon: string;
    name: string;
  };
  toTokenAddress: string;
  chainId: number;
  fromTokenAddress: string;
  txHash: string;
  fromToken: {
    symbol: string;
    address: string;
    chainId: number;
    price: number;
    decimals: number;
    icon: string;
    name: string;
  };
  status: number;
  createdAt: string;
  timestamp: number;
  createTime: number;
}

export interface PerpsOrderInfo {
  symbol: string;
  amount: number;
  side: string;
  isMarket: boolean;
}

// 直播间合约订单
export interface HyperliquidRoomOrder {
  id: number;
  orderId: string;
  userId: number;
  walletAddress: string;
  orderData: string; // JSON字符串
  showData: string; // JSON字符串
  roomId: number;
  isFollow: number;
  createTime: string;
}

// ========== 直播间 Polymarket 预测订单（GET /app/prediction/room/orders）==========

/** orderRequest JSON 中的 showData */
export interface PolymarketOrderShowData {
  title?: string;
  slug?: string;
  icon?: string;
  [key: string]: unknown;
}

/** orderRequest JSON 解析后的结构 */
export interface PolymarketOrderRequestParsed {
  showData?: PolymarketOrderShowData;
  side?: string; // BUY | SELL，前端映射为 Yes / No
  size?: string;
  price?: string;
  amount?: string;
  shares?: string;
  [key: string]: unknown;
}

/** 直播间 Polymarket 订单列表项（接口返回单条） */
export interface PolymarketRoomOrderItem {
  id: number;
  orderId: string;
  boid: string;
  userId: number;
  roomId: number;
  orderType: string; // limit | market
  orderRequest: string; // JSON 字符串，需解析
  status: string; // pending | completed | cancelled | failed
  createTime: string;
  updateTime: string;
  userAvatar: string;
  userName: string;
  showData: string;
}

/** 直播间 Polymarket 订单列表请求参数 */
export interface PolymarketRoomOrdersParams {
  roomId: number;
  page?: number;
  limit?: number;
  filterType?: 'all' | 'streamer' | 'self' | 'others';
}

/** 直播间 Polymarket 订单列表响应 data */
export interface PolymarketRoomOrdersData {
  totalCount: number;
  pageSize: number;
  totalPage: number;
  currPage: number;
  list: PolymarketRoomOrderItem[];
}

/** 用户 Polymarket PNL/胜率统计 - 单个周期 */
export interface PolymarketUserStatsPeriod {
  pnl: number;
  tradeCount: number;
}

/** 用户 Polymarket PNL 和胜率统计响应 data（GET /app/prediction/user/stats） */
export interface PolymarketUserStatsData {
  source: string;
  userId: number;
  handle?: string | null;
  today: PolymarketUserStatsPeriod;
  last7Days: PolymarketUserStatsPeriod;
  last30Days: PolymarketUserStatsPeriod;
  winRate: number; // 0 ~ 1
  totalMarkets: number;
  wonMarkets: number;
}

/** 用户 Polymarket 统计请求参数 */
export interface PolymarketUserStatsParams {
  targetUserId?: number;
  source?: string;
}

// 扫链字段
export interface ScanChain {
  bigLogo: string;
  bots: string;
  bundHoldRto: string;
  ca: string;
  carCnt: string;
  chain: string;
  collect: string;
  createTime: string;
  creator: string;
  creatorMigrCnt: string;
  devHoldRto: string;
  fee: string;
  flwCarCnt: string;
  hold: string;
  logo: string;
  mcap: string;
  media: {
    discord?: {
      url: string;
    };
    facebook?: {
      url: string;
    };
    insta?: {
      url: string;
    };
    tg?: string;
    tt?: {
      url: string;
    };
    twitch?: {
      url: string;
    };
    web?: string;
    x?: {
      url: string;
    };
    youtb?: {
      url: string;
    };
  };
  migrBegin: string;
  migrEnd: string;
  name: string;
  prog: string;
  projLink: string;
  protoId: string;
  rankTypes: string[];
  repostTimestamp: number;
  repostType: number;
  smbl: string;
  sniperBuy: string;
  sniperCnt: string;
  suspHoldRto: string;
  t: [];
  top10HoldRto: string;
  txs1h: string;
  txsb1h: string;
  txss1h: string;
  vol1h: string;
}

export interface PostRanking {
  talkId: number;
  userId: number;
  username: string;
  avatar: string;
  content: string;
  img: string;
  videoUrl?: string;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  shareCount: number;
  userLabels: string;
}

export interface StreamerRanking {
  userId: number;
  avatar: string;
  username: string;
  personalSignature: string;
  followerCount: number;
  followingCount: number;
  postCount: number;
  isLive: boolean;
  totalUserCount: number;
  userLabels: string;
  handle?: string;
}

export interface TokenDetail {
  marketCap: string;
  tokenSymbol: string;
  change: string;
  explorerUrl: string;
  tokenName: string;
  liquidity: string;
  chainIndex: string;
  tagList: {
    communityRecognized: boolean;
  };
  firstTradeTime: string;
  holders: string;
  tokenLogoUrl: string;
  price: string;
  tokenContractAddress: string;
  decimal: string;
  txs: string;
  txsBuy: string;
  txsSell: string;
  volume: string;
  uniqueTraders: string;
  riskLevel: string;
}

export interface Referrals {
  userId: number;
  userName: string;
  avatar?: string;
  invitationCode: string;
  walletNetwork: string;
  joinTime: string;
  totalTransactionUsd: number;
  totalGiftUsd: number;
  totalRewardUsd: number;
  totalPoints: number;
  followStatus: 0 | 1 | 2;
  userLabels: string;
  handle?: string;
}
export interface RankingReferrals {
  ranking: number;
  userId: number;
  userName: string;
  avatar: string;
  totalInviteCount: number;
  followStatus: 0 | 1 | 2;
  userLabels: string;
}

export interface Group {
  id: number;
  groupId: string;
  groupName: string;
  faceUrl: string;
  memberCount: number;
  maxMemberCount: number;
  groupType: number;
  ownerId: number;
  status: number;
  createdAt: string;
  value?: string;
  label?: string;
}

export const devToken =
  'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJzdWIiOiIxMDExIiwiaWF0IjoxNzUyMzk5NTM3LCJleHAiOjE3NTQ5OTE1MzcsInRva2VuSWQiOiI0ZmI4MzI5ZDY0ZGQ0NTVjYTU3NWI0Y2IwYzRhZGNmZiJ9.pL7TgTxgNnQePAD0MPW_DCGeRlNaqDUXAPMQQpQb96S-zudejrQouvkybEJ0eBykdBFq1f8jm59DO02XRECTmg';

export const getUserToken = () => {
  try {
    let userStore = localStorage.getItem('user-store');
    if (userStore) {
      userStore = JSON.parse(userStore);
    }
    return userStore?.state?.token;
  } catch (e) {
    return null;
  }
};

// 白名单接口：匹配到且没有 token，则不请求
// 兼容不同前缀（如 /api/app/...）与 querystring
const WHITE_LIST_PATHS = [
  '/app/gambit/recordTalkView',
  '/app/gambit/batchRecordTalkView',
  '/app/gambit/addShare',
  '/app/gambit/recordExternalShare',
  '/app/room/recordWatchHistory',
  '/app/favorite/markRead',
];

function isWhitelistedEndpoint(endpoint: string) {
  const noQuery = endpoint.split('?')[0];
  const normalized = noQuery.startsWith('/api/') ? noQuery.slice('/api'.length) : noQuery;
  return WHITE_LIST_PATHS.some(p => normalized.includes(p));
}
/** 轮询/高频接口：不参与 request 埋点 */
const REQUEST_CAPTURE_SKIP_PATHS = new Set(['/app/message/v2/getUnreadMessageCount']);

// HTTP客户端类
class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = getUserToken();
  }

  // 重置token
  resetToken(token: string) {
    this.token = token;
  }
  // 通用请求方法
  private async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    let url = `${this.baseURL}${endpoint}`;
    if (isWhitelistedEndpoint(endpoint) && !this.token) {
      console.log('白名单接口，但没有token', endpoint);
      return;
    }
    if (!this.token) {
      this.token = getUserToken();
    }
    const headers: Record<string, string> = {
      token: this.token || '',
      ...(options.headers as Record<string, string>),
    };
    // 如果不是 FormData 类型的请求，则设置 Content-Type
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    } else {
      url = process.env.NEXT_PUBLIC_Local_API_URL + endpoint;
    }
    const pathForCapture = endpoint.length > 300 ? endpoint.slice(0, 300) : endpoint;
    const skipRequestCapture = REQUEST_CAPTURE_SKIP_PATHS.has(endpoint.split('?')[0]);
    let captured = false;
    const emitCapture = (p: { status: 'success' | 'fail'; code?: number; msg?: string }) => {
      if (skipRequestCapture) return;
      if (captured) return;
      captured = true;
      captureRequest({
        action: pathForCapture,
        status: p.status,
        code: p.code,
        msg: p.msg,
      });
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });
      if (!response.ok) {
        emitCapture({
          status: 'fail',
          code: response.status,
          msg: `HTTP ${response.status}`,
        });
        throw new Error(`HTTP错误! 状态: ${response.status}`);
      }

      const data = await response.json();
      // 清除无效的认证信息
      if (data.code === 401) {
        // 只在客户端环境执行
        if (typeof window !== 'undefined') {
          // 打印日志
          console.log('接口无登录态', url);
          // App WebView 环境：不执行 token 销毁和跳转，由 App 端管理登录状态
          if (isInAppWebView()) {
            // App 环境：不调用 logout，不跳转登录页
            // 通知 App 端 token 已过期，由 App 决定是否重新登录
            console.warn('[API] 401 in App WebView, token may be expired');
            // 通过 Bridge 通知 App（App 端已注册该方法，当前仅记录日志）
            (window as any).BillionApp?.call('onTokenExpired', { source: 'api_401' });
          }
          // 为了防止出现推出登录后，还有会出现进度中的网络请求回传，再次出现这个弹窗
          // 改为 仅有token时，才显示这个弹窗
          else {
            if (getUserToken()) {
              toast({
                title: i18next.t('apiAuthFailed'),
                description: i18next.t('apiAuthFailedDesc'),
                variant: 'destructive',
              });

              // 清除缓存 - 直接调用store方法
              const { clearUserInfo } = useUserStore.getState();
              clearUserInfo();
              // 清除token
              this.resetToken('');

              // 跳转到介绍页面
              window.location.href = '/login/';
            } else {
              goToLogin();
            }
          }
        }
        emitCapture({
          status: 'fail',
          code: 401,
          msg: typeof data.msg === 'string' ? data.msg.slice(0, 200) : undefined,
        });
        return Promise.reject(new Error(data.msg || i18next.t('apiAuthFailedDesc')));
      }
      const businessOk = typeof data.code !== 'number' || data.code === 200;
      emitCapture({
        status: businessOk ? 'success' : 'fail',
        code: typeof data.code === 'number' ? data.code : response.status,
        msg: !businessOk && typeof data.msg === 'string' ? data.msg.slice(0, 200) : undefined,
      });
      return data;
    } catch (error) {
      if (!captured) {
        emitCapture({
          status: 'fail',
          msg: error instanceof Error ? error.message.slice(0, 200) : 'unknown',
        });
      }
      console.error('API请求错误:', error);
      throw error;
    }
  }

  // GET请求
  async get<T = any>(endpoint: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    let query = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      query = queryParams.toString();
    }
    return this.request<T>(query ? `${endpoint}?${query}` : endpoint, {
      method: 'GET',
    });
  }

  // POST请求
  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT请求
  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE请求
  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // upload
  async upload<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const formData = new FormData();
    console.log('data', data);
    formData.append('file', data);
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
    });
  }
}

// 创建API客户端实例
export const apiClient = new ApiClient(API_BASE_URL);

// 导出WebSocket URL供其他组件使用
export const getWebSocketEndpoint = () => WS_URL;

// API服务
export const api = {
  // 重置token
  resetToken: (token: string) => {
    apiClient.resetToken(token);
  },

  // 统一对象存储接口（支持 COS/S3/OSS 等多厂商）
  storage: {
    // 获取当前用户的上传配额
    getQuota: () => {
      return apiClient.post<{
        templateName: string;
        maxVideoSize: number;
        maxImageSize: number;
        maxGeneralSize: number;
        dailyVideoLimit: number;
        dailyImageLimit: number;
        dailyTotalSizeLimit: number;
      }>(`/app/storage/quota`);
    },
    // 简单上传签名（小文件 PUT）- 返回预签名 URL
    // width/height：图片类 uploadType（picture/thumbnail/screenshot/avatar）成对传入且均 > 0 时，
    // 后端会把 {w}_{h} 写入 objectKey 供下游解析维度
    signPut: (params: { fileName: string; contentType: string; fileSize?: number; uploadType: string; width?: number; height?: number }) => {
      return apiClient.post<{
        provider: string;
        presignedUrl: string;
        storageHost: string;
        objectKey: string;
        fileUrl: string;
      }>(`/app/storage/sign/put`, params);
    },
    // 分片上传 - 初始化签名（返回预签名 URL + objectKey + 分片信息）
    signInit: (params: { fileName: string; contentType: string; fileSize?: number; uploadType: string; width?: number; height?: number }) => {
      return apiClient.post<{
        provider: string;
        presignedUrl: string;
        storageHost: string;
        objectKey: string;
        fileUrl: string;
        totalParts: number;
        partSize: number;
      }>(`/app/storage/sign/init`, params);
    },
    // 分片上传 - 批量分片预签名 URL（一次性返回所有分片的预签名 URL）
    signBatchParts: (params: { objectKey: string; uploadId: string; totalParts: number }) => {
      return apiClient.post<{
        provider: string;
        storageHost: string;
        objectKey: string;
        partUrls: Record<number, string>;
      }>(`/app/storage/sign/batch-parts`, params);
    },
    // 分片上传 - 完成签名（返回预签名 URL）
    signComplete: (params: { objectKey: string; uploadId: string }) => {
      return apiClient.post<{
        provider: string;
        presignedUrl: string;
        host: string;
        objectKey: string;
        fileUrl: string;
      }>(`/app/storage/sign/complete`, params);
    },
    // 分片上传 - 查询已上传分片（断点续传，返回预签名 URL）
    signListParts: (params: { objectKey: string; uploadId: string }) => {
      return apiClient.post<{
        provider: string;
        presignedUrl: string;
        host: string;
        objectKey: string;
      }>(`/app/storage/sign/list-parts`, params);
    },
  },
  rank: {
    getLiveRanking: (params: { page: number; limit: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: StreamerRanking[];
      }>(`/app/ranking/anchor`, params);
    },
    getPostRanking: (params: { page: number; limit: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: PostRanking[];
      }>(`/app/ranking/hotPost`, params);
    },
  },
  scan: {
    getScanData: (params: { rankType: number; chainId: number; protocolIdList: string }) => {
      return apiClient.get<{
        data: ScanChain[];
      }>(`/app/dex/okx/meme/ranking/content`, params);
    },
  },
  luckybag: {
    create: (body: LuckyBagCreateBody) => apiClient.post<LuckyBagCreateResult>(`/app/luckybag/create`, body),
    cancel: (body: { luckyBagId: number }) => apiClient.post<unknown>(`/app/luckybag/cancel`, body),
    getAnchorRecords: (params?: { page?: number; limit?: number; status?: number }) =>
      apiClient.get<AnchorLuckyBagRecordsPage>(`/app/luckybag/anchor/records`, params as Record<string, unknown>),
    getAnchorRecordDetail: (params: { luckyBagId: number }) =>
      apiClient.get<AnchorLuckyBagDetail>(`/app/luckybag/anchor/record-detail`, params as Record<string, unknown>),
    listGroup: (params?: { groupType?: number }) => apiClient.get<Group[]>(`/app/luckybag/anchor/groups`, params as Record<string, unknown>),
    confirmHash: (body: { luckyBagId: number; txHash: string }) => apiClient.post<unknown>(`/app/luckybag/confirm-tx`, body),
    getLuckyBagInfo: (params: { roomId: string }) => apiClient.get<LuckyBagInfo>(`/app/luckybag/active`, params),
    getLuckyBagPrecondition: (params: { luckyBagId: number }) => apiClient.get<LuckyBagViewerPreconditionData>(`/app/luckybag/precondition`, params),
    getViewerRecords: (params?: { page?: number; limit?: number }) =>
      apiClient.get<LuckyBagViewerRecordsPage>(`/app/luckybag/viewer/records`, params as Record<string, unknown>),
    /** F6.6 中奖用户在直播间内点击领取 */
    claim: (body: { luckyBagId: number }) => apiClient.post<LuckyBagClaimResult>(`/app/luckybag/claim`, body),
  },
  chat: {
    /** 主播创建福袋等场景：可选 TRTC 公开群列表 */
    list: (params?: Record<string, unknown>) => apiClient.get<AppChatListItem[] | { list: AppChatListItem[] }>(`/app/chat/list`, params),
  },
  // Banner相关接口
  banner: {
    // 获取Banner列表
    getBanners: () => {
      return apiClient.get<Banner[]>(`/app/room/selectHotRoomListWithIVS`);
    },
  },

  // 分类接口
  category: {
    getHotLiveCategory: (params: { page: number; limit: number }) => {
      return apiClient.get<Category[]>(`/app/hotLiveCategory/list`, params);
    },
  },

  // 直播间相关接口
  room: {
    // 获取访客签名
    getGuestUserSig: () => {
      return apiClient.get<{
        guestUserId: string;
        guestUserSig: string;
      }>(`/app/room/generateGuestUserSig`);
    },
    getRoomReplaysByUserId: (params: { page: number; limit: number; currentUserId: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/getRoomReplaysByUserId`, params);
    },
    // 直播回放
    getMyRoomReplays: (params: { page: number; limit: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/getMyRoomReplays`, params);
    },
    // 删除直播回放
    deleteLiveReplay: (params: { roomId: number }) => {
      return apiClient.post(`/app/room/deleteLiveReplay`, params);
    },
    // 记录观看记录
    recordWatchHistory: (params: { roomId: number }) => {
      return apiClient.post(`/app/room/recordWatchHistory`, params);
    },
    // 获取观看记录
    getMyWatchHistory: (params: { page: number; limit: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: HistoryWatchLiveRoom[];
      }>(`/app/room/getMyWatchHistory`, params);
    },
    // 获取分类
    getCategoryListWithRooms: (params: { limit: number }) => {
      return apiClient.get<CategoryRoom[]>(`/app/room/getCategoryListWithRooms`, params);
    },
    getCategoryListWithRoomsV2: (params: { limit: number }) => {
      return apiClient.get<CategoryRoomNew[]>(`/app/room/getCategoryListWithRoomsV2`, params);
    },
    // 获取推荐主播
    getPromotionRooms: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/selectPromotionRoomList`, params),

    // 获取关注主播
    getMyFollowRooms: (params: { page: number; limit: number; type?: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/getMyFollowLiveList`, params),

    getIndexBannerRoomList: (params: { limit: number }) => {
      return apiClient.get<LiveRoom[]>(`/app/room/getIndexBannerRoomList`, params);
    },
    getHomeRoom: (params: { page: number; limit: number; status?: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/selectHotRoomListWithIVS`, params);
    },
    // 获取直播间列表
    getRoomList: (params: {
      page: number;
      limit: number;
      userId?: number;
      roomNo?: string;
      roomName?: string;
      userName?: string;
      status?: number;
      roomType?: number;
      search?: string;
      sort?: number;
    }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/selectRoomList`, params);
    },

    // 通用接口
    uploadLiveCover: (file: File) => {
      return apiClient.upload(`/app/room/uploadFile`, file);
    },

    // 上传视频文件
    uploadVideo: (file: File) => {
      return apiClient.upload(`/app/room/uploadVideo`, file);
    },
    // 获取直播间详情
    getCurrentLiveStatus: () => apiClient.get<LiveRoom>(`/app/room/getCurrentLiveStatus`),

    // 轮询直播间状态
    pollLiveStatus: (params: { roomId: number }) =>
      apiClient.get<{
        status: number;
        roomId: number;
      }>(`/app/room/checkUserLiveStatus`, params),
    // 获取直播间评论
    selectRoomChatList: (params: { page: number; limit: number; roomId: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: RoomChat[];
      }>(`/app/room/selectRoomChatList`, params),

    // 增量拉取聊天记录-根据最后消息ID获取之后的新消息
    selectRoomChatListAfterId: (params: { roomId: number; lastRoomChatId: number; limit?: number }) =>
      apiClient.get<RoomChat[]>(`/app/room/selectRoomChatListAfterId`, params),

    // 获取直播间详情
    getRoomDetails: (roomId: string) =>
      apiClient.get<{
        stopSecond: number;
        coinPriceList: CoinPrice[];
        room: LiveRoom;
        playbackUrl: string;
        flvStreamUrl?: string;
        streamerEvmWalletAddress: string;
        streamerSolWalletAddress: string;
        streamerAddress: string;
        platformAddress: string;
        platformFeePercentage: number;
        streamerInfo: StreamerInfo;
        relationshipStatus: FollowStatus; // 0: 未关注 1: 已关注 2: 互相关注
        recordingVideos?: RecordingVideo[];
      }>(`/app/room/getRoomDetailInfo?roomId=${roomId}`),

    // 设置直播间公告
    setAnnouncement: (params: { roomId: number; announcement: string | null }) =>
      apiClient.post<{ announcement: string | null; action: string }>(`/app/room/setAnnouncement`, params),

    // 直播间币种链接
    getRoomCoinPrice: (params: { roomId: string }) => apiClient.get(`/app/contractReward/chainConfigs`, params),

    // 获取直播间人数、礼物列表详情
    getRoomGiftRankingList: (params: { roomId: string; type: number }) =>
      apiClient.get<{
        currentRoomUserCount: number;
        rankingList: GiftUserRank[];
        myRanking: GiftUserRank;
        totalViewerCount: number;
      }>(`/app/room/getRoomGiftRankingList`, params),

    // 获取热门主播
    getHotRooms: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/selectHotRoomList?page=${page}&limit=${limit}`),

    // 随机匹配房间
    getRandomRoom: () => apiClient.get<LiveRoom>(`/app/room/selectRandomRoom`),

    // 历史直播间
    selectMyLiveHistoryList: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/selectMyLiveHistoryList`, params),

    // 获取首页汇聚内容列表（融合热门直播间和精选动态）
    getFeaturedContentList: (params: { page?: number; size?: number; videoLimit?: number; sortType?: string }) => {
      return apiClient.get<{
        list: FeaturedContentItem[];
        currPage: number;
        pageSize: number;
        totalCount: number;
        totalPage: number;
      }>(`/app/room/getFeaturedContentList`, params);
    },

    // 随机匹配房间
    createLiveRoom: (data: LiveRoom) =>
      apiClient.post<{
        userSig: string;
        sdkAppId: number;
        playbackUrl: string;
        obsStreamUrl: string;
        roomId: number;
        obsStreamKey: string;
        obsServer: string;
      }>(`/app/room/createLiveRoom`, data),

    /** 创建直播预约（需登录） */
    createLiveSchedule: (data: CreateLiveSchedulePayload) => apiClient.post<CreateLiveScheduleData>(`/app/room/schedule/create`, data),

    /** 我的直播预约列表（需登录） */
    getMyScheduleList: (params?: { page?: number; limit?: number; status?: number }) =>
      apiClient.get<LiveScheduleListData>(`/app/room/schedule/myList`, {
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
        status: params?.status ?? undefined,
      } as Record<string, unknown>),

    /** 当前用户已预约的直播预告列表（需登录） */
    getMyAppointmentList: (params?: { page?: number; limit?: number }) =>
      apiClient.get<MyAppointmentListData>(`/app/room/schedule/myAppointmentList`, {
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
      } as Record<string, unknown>),

    /** 热门预约列表（需登录） */
    getHotAppointmentList: (params?: { page?: number; limit?: number }) =>
      apiClient.get<HotAppointmentListData>(`/app/room/schedule/hotAppointmentList`, {
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
      } as Record<string, unknown>),

    /** 预约详情（文档为无需登录，具体以后端为准） */
    getLiveScheduleDetail: (scheduleId: number) =>
      apiClient.get<LiveScheduleDetail>(`/app/room/schedule/detail`, { scheduleId } as Record<string, unknown>),

    /** 观众预约直播（需登录） */
    appointLiveSchedule: (scheduleId: number) => apiClient.post<unknown>(`/app/room/schedule/appoint`, { scheduleId }),

    /** 观众取消预约（需登录） */
    cancelAppointLiveSchedule: (scheduleId: number) => apiClient.post<unknown>(`/app/room/schedule/cancel-appoint`, { scheduleId }),

    /** 更新直播预约（字段与创建一致；路径若与后端不一致请调整） */
    updateLiveSchedule: (data: UpdateLiveSchedulePayload) => apiClient.post<unknown>(`/app/room/schedule/update`, data),

    /** 关闭/下架直播预约（需登录，不可逆；关联帖子同步下架） */
    closeLiveSchedule: (scheduleId: number) => apiClient.post<unknown>(`/app/room/schedule/close?scheduleId=${scheduleId}`),

    // 关闭直播
    stopLiveWithTRTC: (roomId: number) => apiClient.post(`/app/room/stopLiveWithTRTC`, { roomId }),

    // 获取直播间聊天记录
    getRoomChatList: (params: { page: number; limit: number; roomId: number; content?: string; userId?: number }) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: RoomChat[];
      }>(`/app/room/selectRoomChatList?${queryParams.toString()}`);
    },

    // 加入直播间
    joinRoom: (roomId: number) => apiClient.post(`/app/room/insertRoomUser`, { roomId }),

    // 离开直播间
    leaveRoom: (roomUserId: number) => apiClient.post(`/app/room/updateRoomUser`, { roomUserId, status: 2 }),

    // 获取直播间订单列表
    getRoomOrderList: (params: { roomId: number; limit: number; page: number; messageType?: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: any[];
      }>(`/app/room/selectRoomChatListByMessageType`, params);
    },

    // 记录直播间分享
    shareRoom: (roomId: number) =>
      apiClient.post<{
        shareCount: number;
      }>(`/app/room/shareRoom`, { roomId }),

    // 字幕设置：获取当前用户在房间内的字幕设置
    getSubtitleSetting: (roomId: string | number) =>
      apiClient.get<{
        enabled: boolean;
        mode: 'off' | 'translate_only' | 'original_and_translate';
        targetLang: 'zh' | 'en' | 'ko';
        taskRunning: boolean;
        supportedLangs: string[];
        supportedModes: string[];
      }>(`/app/room/${roomId}/subtitle/setting`),

    // 字幕设置：开启
    enableSubtitle: (
      roomId: string | number,
      body: {
        mode?: 'off' | 'translate_only' | 'original_and_translate';
        targetLang?: 'zh' | 'en' | 'ko';
        sourceLang?: string | null;
      }
    ) => apiClient.post<null>(`/app/room/${roomId}/subtitle/enable`, body),

    // 字幕设置：关闭
    disableSubtitle: (roomId: string | number) => apiClient.post<null>(`/app/room/${roomId}/subtitle/disable`, {}),

    // 字幕设置：更新（语言或模式切换）
    updateSubtitleSetting: (
      roomId: string | number,
      body: {
        mode?: 'off' | 'translate_only' | 'original_and_translate';
        targetLang?: 'zh' | 'en' | 'ko';
        enabled?: boolean;
        sourceLang?: string | null;
      }
    ) => apiClient.post<null>(`/app/room/${roomId}/subtitle/update`, body),
  },

  // 用户相关接口
  user: {
    getTwitterAuthUrl: (params: { redirectUri: string }) =>
      apiClient.get<{
        authorizationUrl: string;
        state: string;
      }>(`/app/twitter/auth-url`, params),
    unlinkTwitter: () => apiClient.post(`/app/user/unbindTwitter`),
    getTwitterAuthCallback: (params: { code: string; state: string; redirectUri: string }) =>
      apiClient.post<{
        twitterId: string;
        twitterUsername: string;
      }>(`/app/twitter/callback`, params),
    // 激活用户记录
    getPointsUserList: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: {
          wallet_network: string;
          invitation_code: string;
          create_time: string;
          user_id: number;
          user_name: string;
          totalPoints: number;
          avatar: string;
        }[];
      }>(`/app/wallet/invited-users`, params),
    // 绑定邀请码
    bindInviteCode: (inviterCode: string, isSkipBind: boolean = false) => apiClient.post(`/app/wallet/bind-inviter`, { inviterCode, isSkipBind }),
    // 申请人工绑定邀请码（绑定窗口过期后，同一用户仅一条待审核）
    applyManualBindInvite: (body: { inviterCode: string; reason?: string }) => apiClient.post<null>(`/app/wallet/apply-manual-bind`, body),
    // 获取用户积分记录
    getPointsStatistics: () =>
      apiClient.get<{
        totalPoints: number;
        monthEarnedPoints: number;
        inviteCount: number;
        currentMonth: string;
      }>(`/app/user/points/statistics`),
    // 获取用户积分记录
    getPointsRecords: (params: { page: number; limit: number }) =>
      apiClient.get<{
        page: {
          totalCount: number;
          pageSize: number;
          totalPage: number;
          currPage: number;
          list: PointsRecord[];
        };
      }>(`/app/user/points/details`, params),
    // 获取用户详细信息
    getUserInfo: () => apiClient.get<UserProfile>(`/app/user/getUserProfile`),
    /** 检查 handle 可用性 */
    checkHandle: (params: { handle: string }) => apiClient.get<null>(`/app/user/checkHandle`, params),
    /** 修改 handle（一次性） */
    updateHandle: (body: { handle: string }) => apiClient.post<null>(`/app/user/updateHandle`, body),
    // 获取个人信息概览（用户总价值、会员等级、邀请排名、积分排名）
    getUserProfileOverview: () => apiClient.get<UserProfileOverviewData>(`/app/user/getUserProfileOverview`),
    // 根据用户ID获取用户信息
    getUserInfoById: (params: { userId?: number; handle?: string }) => apiClient.get<UserProfile>(`/app/user/getUserProfileByUserId`, params),
    // 退出
    logout: () => apiClient.post(`/app/wallet/logout`),

    // 更新用户信息
    updateUser: (userInfo: Partial<UserInfo>) => apiClient.post(`/app/user/updateUserProfile`, userInfo),

    // 更新用户头像
    uploadAvatar: (file: File) => {
      return apiClient.upload(`/app/user/uploadAvatar`, file);
    },

    // 成为主播
    becomeStreamer: (data: BecomeStreamer) => apiClient.post(`/app/user/streamer/apply`, data),

    // 获取主播申请状态
    getStreamerApplyStatus: () => apiClient.get(`/app/user/streamer/status`),

    // 获取 2FA 绑定状态
    getTwoFaStatus: () => apiClient.get<{ bound: boolean }>(`/app/twoFa/status`),
    // 获取 2FA 初始化信息
    getTwoFaBindInit: () => apiClient.get<{ secretKey: string; qrCodeUri: string; secretKeyExpireAt?: number }>(`/app/twoFa/bind/init`),
    // 确认 2FA 绑定
    postTwoFaBindConfirm: (totpCode: string) => apiClient.post<{ recoveryCodes: string[] }>(`/app/twoFa/bind/confirm`, { totpCode }),
    // 验证 2FA 验证码
    postTwoFaVerify: (totpCode: string) => apiClient.post<{ valid: boolean }>(`/app/twoFa/verify`, { totpCode }),
    // 解绑 2FA（需先验证通过）
    postTwoFaUnbind: (code: string) => apiClient.post<null>(`/app/twoFa/unbind`, { recoveryCode: code }),

    // 获取消息数量
    getMessageCount: () => apiClient.get(`/app/message/getUnreadMessageCount`),
    // 获取消息列表
    getUserMessages: (params: { page: number; limit: number }) => apiClient.get(`/app/message/getUserMessages`, params),
    // 获取提醒列表
    getNotificationList: (params: { page: number; limit: number; messageType?: string }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Notification[];
      }>(`/app/message/getMessagesByType`, params),
    // 获取所有未读消息汇总（统一消息接口）
    getAllUnreadMessages: () =>
      apiClient.get<{
        announcement: UnifiedMessage[];
        system: UnifiedMessage[];
        transaction: UnifiedMessage[];
        unreadCount: {
          announcement: number;
          system: number;
          transaction: number;
        };
      }>(`/app/message/getAllUnreadMessages`),
    // 获取分类消息
    getCategoryMessages: (params: { page: number; limit: number; category?: string; readStatus?: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UnifiedMessage[];
      }>(`/app/message/getMessagesByCategory`, params),
    // 标记消息为已读
    markAsRead: (params: { messageId: number; messageType?: number }) => apiClient.post(`/app/message/markAsRead`, params),
    // 标记全部消息为已读
    markAllAsRead: () =>
      apiClient.post<{
        markedCount: number;
      }>(`/app/message/markAllAsRead`),

    // ===== V2 消息接口 =====
    // V2: 未读消息数
    getMessageCountV2: () => apiClient.get(`/app/message/v2/getUnreadMessageCount`),
    // V2: 未读消息汇总（四分类 + all）
    getMessageSummaryV2: (params?: { page?: number; limit?: number }) =>
      apiClient.get<{
        all: UnifiedMessage[];
        allCurrPage: number;
        allPageSize: number;
        allTotalCount: number;
        allTotalPage: number;
        unreadCount: {
          all: number;
          announcement: number;
          system: number;
          account: number;
          interactions: number;
        };
      }>(`/app/message/v2/summary`, params),
    // V2: 按分类分页查询消息, viewMode:点赞等消息是聚合还是单条
    getMessagesV2: (params: { category: string; readStatus: number; page?: number; limit?: number; viewMode?: string }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UnifiedMessage[];
      }>(`/app/message/v2/messages`, params),
    // V2: 标记单条消息已读
    markAsReadV2: (params: { messageId: number; messageType: number }) => apiClient.post(`/app/message/v2/mark-read`, params),
    // V2: 全部消息标记已读
    markAllAsReadV2: () => apiClient.post<{ markedCount: number }>(`/app/message/v2/mark-all-read`),
    // V2: 批量已读 / 按分类一键已读
    batchMarkAsReadV2: (params: { category?: string; messages?: { messageId: number; messageType: number }[] }) =>
      apiClient.post<{ successCount: number; failedCount: number; failedMessages: any[] }>(`/app/message/v2/batch-mark-read`, params),

    // 我收藏的接口
    getMyFavorites: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Post[];
      }>('/app/gambit/getMyFavoriteTalkList', params),

    // 我点赞的接口
    getMyLikedTalkList: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Post[];
      }>('/app/gambit/getMyLikedTalkList', params),

    // 提交反馈
    submitFeedback: (data: { problemDescription: string; problemPage: string; screenshotUrl: string; problemType: string; contactEmail: string }) =>
      apiClient.post(`/app/feedback/submit`, data),

    // 获取反馈列表
    getFeedbackList: (data: { page: number; limit: number; status?: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Feedback[];
      }>(`/app/feedback/myList`, data),

    // 获取反馈详情（包含留言列表）
    getFeedbackDetail: (params: { feedbackId: number }) => apiClient.get<FeedbackDetailResponse>(`/app/feedback/detail`, params),

    // 用户对反馈进行留言
    leaveMessage: (data: { feedbackId: number; content: string }) => apiClient.post(`/app/feedback/leaveMessage`, data),

    // 用户关闭工单
    closeFeedback: (data: { id: number }) => apiClient.post(`/app/feedback/close`, data),

    // 用户对工单处理结果进行评分和反馈
    rateFeedback: (data: { id: number; satisfactionScore: number; ratingComment?: string }) => apiClient.post(`/app/feedback/rate`, data),

    // 获取用户邀请记录
    getMyRecommendations: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Referrals[];
      }>(`/app/wallet/my-recommendations`, params),
    // 获取排行榜
    getReferralsRanking: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: RankingReferrals[];
      }>(`/app/wallet/invitation-ranking`, params),
    // 更新拉黑状态
    updateBlockStatus: (params: { id: number; isBlock: boolean }) => {
      const url = params.isBlock ? '/app/userBlock/block' : '/app/userBlock/unblock';
      return apiClient.post<void>(url, { blockedUserId: params.id });
    },

    // 生成登录码
    generateLoginCode: () =>
      apiClient.post<{
        loginCode: string;
        expireTime?: number;
        expireSeconds?: number;
      }>(`/app/user/assistant/generateLoginCode`),

    // 检查推流码状态
    checkLoginCode: () =>
      apiClient.get<{
        exists: boolean; // 是否存在
        isUsed: boolean; // 是否已使用
        loginCode?: string; // 推流码（如果存在且未使用）
        remainingSeconds?: number; // 剩余时间（秒）
      }>(`/app/user/assistant/checkLoginCode`),
  },

  // 用户关注相关接口
  follow: {
    // 关注/取消关注用户
    unFollow: (followUserId: number) => apiClient.post(`/app/userFollow/insert`, { followUserId }),

    // 获取关注、粉丝数
    getMyFansAndFollowInfo: () =>
      apiClient.get<{
        fansTotalCount: number;
        followTotalCount: number;
      }>(`/app/userFollow/getMyFansAndFollowInfo`),

    // 获取我的关注列表
    getMyFollows: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userFollow/getMyFollowList?page=${page}&limit=${limit}`),

    // 获取我的关注列表
    getMyFansList: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userFollow/getMyFansList?page=${page}&limit=${limit}`),
    getFollowListByUserId: (params: { targetUserId: number; page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userFollow/getFollowListByUserId`, params),
    getFansListByUserId: (params: { targetUserId: number; page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userFollow/getFansListByUserId`, params),
    // 获取我的关注排行榜
    getFollowRanking: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userFollow/getFollowRanking`, {
        page,
        limit,
      }),
    // 获取我的粉丝列表
    getMyFans: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userFollow/selectFans?page=${page}&limit=${limit}`),

    // 推荐关注
    getStreamerRecommend: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserProfile[];
      }>(`/app/userFollow/getStreamerRecommend`, params),
    // 检查是否关注某用户
    checkFollowStatus: (followUserId: number) => apiClient.get<boolean>(`/app/userFollow/selectFollowUser?followUserId=${followUserId}`),
  },

  // 动态相关接口
  post: {
    // 获取精选动态
    getFeaturedTalkList: (params: { page: number; size: number; talkId?: number; sortType?: string }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Post[];
      }>(`/app/gambit/getFeaturedTalkList`, params);
    },

    // 根据ID批量获取动态
    getTalksByIds: (talkIds: string) => {
      return apiClient.get<Post[]>(`/app/gambit/getTalksByIds`, { talkIds });
    },

    // 获取动态详情
    getPostByUserId: (params: { targetUserId: number; page: number; size: number }) => {
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Post[];
      }>(`/app/gambit/getUserTalkList`, params);
    },

    // 获取个人动态
    getMyPosts: async (params: { page: number; size: number; status?: number }) => {
      const res = await apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Post[];
      }>(`/app/gambit/getMyTalkList`, params);
      // if (res?.data?.list) {
      //   res.data.list.unshift({
      //     status: 1,
      //     displayStatus: 'REVIEW_REJECTED', // 待审核
      //     rejectReasonType: 'PORN', // 拒绝原因
      //     takedownSource: 'AUTO', // 下架来源
      //     takedownTime: 1776922200000, // 下架时间
      //     errorImgs: [0, 1, 2],
      //     talkId: 4576,
      //     content: '测试封禁帖子',
      //     imageUrl:
      //       'https://media-static.billion.live/pictures/20260423/user_100385/5f2a588c97e34397b95e8cdb7156e60c.jpeg,https://media-static.billion.live/pictures/20260423/user_100385/31fab014e82647c6b3c5a37c201c0fd5.jpeg,https://media-static.billion.live/pictures/20260423/user_100385/a8b651aed36544f8aadbba46b26651d4.jpeg,https://media-static.billion.live/pictures/20260423/user_100385/1fecfcce8f304166a723ce5c0ba2fce7.jpeg',
      //     videoUrl: null,
      //     videoCover: null,
      //     coverDimension: null,
      //     timestamp: '2026-04-23 13:43:05',
      //     isTop: 0,
      //     topTime: null,
      //     author: {
      //       userId: 100385,
      //       name: 'ChangDou',
      //       avatar: '',
      //       handle: 'ifonlybean',
      //       isOfficial: 0,
      //       level: 1,
      //       fansCount: 1,
      //       followStatus: 0,
      //       userLabels: null,
      //       isRoom: null,
      //       roomId: null,
      //     },
      //     likeCount: 0,
      //     commentCount: 0,
      //     shareCount: 0,
      //     viewCount: 0,
      //     isLiked: 0,
      //     isCollected: 0,
      //     isShared: 0,
      //     cryptoData: {
      //       coinSymbol: null,
      //       coinPrice: null,
      //       coinPriceFormatted: null,
      //     },
      //     polymarketEventSlug: null,
      //     contentCategoryIds: null,
      //     imageList: [
      //       'https://media-static.billion.live/pictures/20260423/user_100385/5f2a588c97e34397b95e8cdb7156e60c.jpeg',
      //       'https://media-static.billion.live/pictures/20260423/user_100385/31fab014e82647c6b3c5a37c201c0fd5.jpeg',
      //       'https://media-static.billion.live/pictures/20260423/user_100385/a8b651aed36544f8aadbba46b26651d4.jpeg',
      //       'https://media-static.billion.live/pictures/20260423/user_100385/1fecfcce8f304166a723ce5c0ba2fce7.jpeg',
      //     ],
      //     tags: [],
      //     commentsList: [],
      //     roomDetailInfo: null,
      //     originalTalkId: null,
      //     otherUserList: [],
      //     quotedTalk: null,
      //     quotedTalkDeleted: null,
      //     repostCount: 0,
      //     aggregatedRepostUsers: null,
      //     scheduleDetail: null,
      //   });
      // }
      return res;
    },

    // 获取动态列表
    getPosts: (params: {
      page: number;
      size: number;
      status?: number;
      userId?: number;
      byUserId?: number;
      followUserId?: number;
      isDelete?: number;
    }) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      return apiClient.get<{
        totalPages: number;
        totalElements: number;
        content: Post[];
      }>(`/app/gambit/selectTalkPage?${queryParams.toString()}`);
    },

    // 获取动态详情
    getPostById: (talkId: number, userId?: number) => {
      const params = new URLSearchParams();
      params.append('talkId', talkId.toString());
      if (userId) params.append('userId', userId.toString());
      return apiClient.get<Post>(`/app/gambit/selectById?${params.toString()}`);
    },

    // 获取talkId获取动态详情
    getTalkRepliesList: (params: { talkId: number; page: number; size: number }) => {
      return apiClient.get<{
        totalPages: number;
        totalElements: number;
        currPage: number;
        pageSize: number;
        list: Comment[];
      }>(`/app/gambit/getTalkRepliesList`, params);
    },
    // 发布动态
    replyPost: (postData: { talkId: number; byUserId?: number; content: string; parentId?: number }) =>
      apiClient.post<Comment>(`/app/gambit/replyComment`, postData),

    // 发布动态&编辑（有talkId）
    createPost: (postData: {
      content: string;
      imageUrl?: string;
      videoUrl?: string;
      cryptoData?: CryptoData;
      tags?: string[];
      talkId?: number;
      roomId?: number;
    }) => apiClient.post<Post>('/app/gambit/saveTalk', postData),

    // 获取动态详情
    getPostDetail: (params: { talkId: number }) => apiClient.get<Post>(`/app/gambit/selectById`, params),

    // 删除动态
    deletePost: (talkId: number) => apiClient.post(`/app/gambit/deleteTalkById`, { talkId }),

    // 下架帖子申请人工重审
    requestReview: (talkId: number) => apiClient.post(`/app/talk/moderation/requestReview`, { talkId }),

    // 点赞动态
    likePost: (talkId: number) => apiClient.post(`/app/gambit/saveGoodTalk`, { talkId }),

    // 分享动态
    sharePost: (talkId: number) => apiClient.post(`/app/gambit/addShare`, { talkId }),
    recordPost: (talkId: number) => apiClient.post(`/app/gambit/recordExternalShare`, { talkId }),

    // 收藏动态
    collectPost: (talkId: number) => apiClient.post(`/app/favorite/toggle`, { talkId }),

    // 帖子已读上报（24小时内同用户同帖子只计1个UV）
    markRead: (talkId: number) => apiClient.post(`/app/favorite/markRead`, { talkId }),
  },

  // 评论相关接口
  comment: {
    // 获取评论列表
    getComments: (params: { page: number; size: number; userId?: number; talkId: number }) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      return apiClient.get<{
        totalPages: number;
        totalElements: number;
        content: Comment[];
      }>(`/app/gambit/selectCommentPage?${queryParams.toString()}`);
    },

    // 发布评论
    createComment: (commentData: { talkId: number; content: string; parentId?: number }) => apiClient.post(`/app/gambit/saveComment`, commentData),

    // 删除评论
    deleteComment: (commentId: number) => apiClient.post(`/app/gambit/deleteCommentById`, { commentId }),

    // 点赞/取消点赞评论
    likeComment: (commentId: number) => apiClient.post<{ liked: boolean; likeCount: number }>(`/app/gambit/likeComment`, { commentId }),
  },

  // 转发相关接口
  forward: {
    // 转发动态到广场
    forwardTalk: (data: { content?: string; originalTalkId: number }) =>
      apiClient.post<{ talkId: number; originalTalkId: number }>(`/app/gambit/forwardTalk`, data),
  },

  // 礼物相关接口
  gift: {
    // 获取我打赏的礼物列表
    getMyGiftRecords: (params: { page: number; limit: number; status: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Gift[];
      }>('/app/gift/order/myGiftRecords', params),
    // 获取我的礼物列表
    getMyGifts: (params: { page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Gift[];
      }>('/app/gift/selectMyGiftIncomeDetail', params),

    // 获取礼物列表
    getGifts: () => apiClient.get<Gift[]>(`/app/gift/getGiftList`),

    // 赠送礼物
    sendGift: (giftData: { giftId: number; userId: number; roomId?: number; talkId?: number; num?: number }) =>
      apiClient.post(`/app/gift/sendGift`, giftData),
    // 创建订单
    createOrder: (params: {
      roomId: number;
      giftId: number;
      deductCoin: string;
      giftQuantity: number;
      coinAmount: number;
      rewardChainConfigId?: number;
      rewardChainTokenId?: number;
    }) => apiClient.post(`/app/gift/order/create`, params),
    getByOrderNo: (orderNo: string) => apiClient.get(`/app/gift/order/getByOrderNo`, { orderNo }),
    // 获取订单详情
    updateStatus: (data: { orderNo: string; orderStatus?: number; transactionHash: string }) => apiClient.post(`/app/gift/order/updateStatus`, data),

    // 计算货币金额
    calculateCurrencyAmount: (params: { currency: string; giftId: number; quantity: number; roomId: string }) =>
      apiClient.post<GiftAmount>(`/app/gift/calculation/calculateCurrencyAmount `, params),
    // 获取直播间礼物记录
    getRoomGifts: (params: { page: number; limit: number; roomId: number }) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      return apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: GiftUser[];
      }>(`/app/gift/selectGiftRoomList?${queryParams.toString()}`);
    },

    // 获取礼物排行榜
    getGiftRanking: (page: number = 1, limit: number = 10, type: number = 1) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: any[];
      }>(`/app/gift/selectReceiveGiftUserList?page=${page}&limit=${limit}&type=${type}`),
  },

  // 货币接口
  dex: {
    // 获取订单信息
    getOrderList: (params: { page: number; limit: number; status: string }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Order[];
      }>(`/app/dex/transaction/records_by_user_id`, params),
    // 获取货币列表
    getDexTokenList: (params: { chainId: number }) => apiClient.get<DexToken[]>(`/app/dex/token/list`, params),
    // 获取货币列表（分页）
    getDexTokenPageList: (params: { page?: number; limit?: number; chainId?: number; keyword?: string; status?: number }) =>
      apiClient.get<{
        list: DexToken[];
        totalCount: number;
        pageSize: number;
        currPage: number;
        totalPage: number;
        statistics: {
          listedCount: number;
          unlistedCount: number;
          totalCount: number;
        };
      }>(`/app/dex/token/pageList`, params),
    // 获取货币列表
    getUserTokenList: (params: { userId: number }) =>
      apiClient.get<{
        tokenListingList: UserShopToken[];
        forecastListingList: { slug: string }[];
      }>(`/app/dex/token/getUserTokenList`, params),

    // 上架
    upToken: (params: { tokenId: number }) => apiClient.post(`/app/dex/token/add`, params),

    // 下架
    downToken: (params: { tokenId: number }) => apiClient.post(`/app/dex/token/remove`, params),

    // 搜索
    searchToken: (params: { search: string; chains: string }) => apiClient.get<TokenDetail[]>(`/app/dex/market/token/search`, params),

    // 获取市场排名
    getMarketRank: (params: { chains: string; sortBy: number; timeFrame: number }) =>
      apiClient.get<TokenDetail[]>(`/app/dex/market/token/toplist`, params),
  },
  // 用户浏览记录相关接口
  browse: {
    // 获取我的访客
    getMyVisitors: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userBrowse/myVisitor?page=${page}&limit=${limit}`),

    // 获取我的浏览记录
    getMyBrowse: (page: number = 1, limit: number = 10) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserFollow[];
      }>(`/app/userBrowse/myBrowse?page=${page}&limit=${limit}`),

    // 获取统计数据（粉丝量、关注量、访客量等）
    getStats: () =>
      apiClient.get<{
        fans: number;
        follow: number;
        visitor: number;
        browse: number;
      }>(`/app/userBrowse/selectAmount`),
  },
  // Hyperliquid 相关接口
  hyperliquid: {
    // 查询钱包授权状态
    getWalletAuthorization: (params: { walletAddress: string }) =>
      apiClient.get<{
        authorizedAddress: string;
        expireTime: number;
        authorized: boolean;
        platformWalletAddress: string;
        message: string;
      }>(`/app/hyperliquid/wallet/authorization`, params),

    // 同步钱包授权状态
    syncWalletAuthorization: (params: { walletAddress: string; authorizedAddress: string; expireTime: number }) =>
      apiClient.post<{
        success: boolean;
        message?: string;
        data?: {
          walletAddress: string;
          authorizedAddress: string;
          expireTime: number;
          syncAt: string;
        };
      }>(`/app/hyperliquid/wallet/authorization/sync`, params),

    // 提交订单
    submitOrder: ({
      showData,
      orderData,
    }: {
      showData: {};
      orderData: {
        orders: Array<{
          a: number;
          b: boolean;
          p: string;
          s: string;
          r: boolean;
          t: {
            limit: {
              tif: string;
            };
          };
        }>;
        grouping: string;
        vaultAddress: string;
      };
    }) =>
      apiClient.post<{
        success: boolean;
        message?: string;
        orderId?: string;
        status?: string;
      }>(`/app/hyperliquid/wallet/order`, {
        showData,
        orderData,
      }),

    // ✅ V2 接口：记录客户端下单数据
    submitOrderV2: ({
      cloid,
      walletAddress,
      orderData,
      showData,
      result,
    }: {
      cloid: string; // ✅ Client Order ID
      walletAddress: string; // ✅ 钱包地址
      orderData: {
        action: any; // Hyperliquid action 对象
        nonce: number;
        signature: {
          r: string;
          s: string;
          v: 27 | 28;
        };
        builder?: {
          b: string; // Builder 地址
          f: number; // Builder fee (0.1bps 为单位)
        };
      };
      showData: any;
      result: any; // Hyperliquid 返回结果
    }) =>
      apiClient.post<{
        success: boolean;
        message?: string;
      }>(`/app/v2/hyperliquid/wallet/order`, {
        cloid,
        walletAddress,
        orderData,
        showData,
        result,
      }),

    // 取消订单
    cancelOrder: (cancelData: {
      operation: string;
      cancels: Array<{
        a: number; // 资产ID
        o: number; // 订单ID
      }>;
      vaultAddress: string;
    }) =>
      apiClient.post<{
        success: boolean;
        message?: string;
        cancelledOrders?: Array<{
          a: number;
          o: number;
          status: string;
        }>;
      }>(`/app/hyperliquid/wallet/order/cancel`, cancelData),

    // 修改杠杆
    updateLeverage: (leverageData: { operation: string; asset: number; isCross: boolean; leverage: number; vaultAddress: string }) =>
      apiClient.post<{
        success: boolean;
        message?: string;
        data?: string;
      }>(`/app/hyperliquid/wallet/order/updateLeverage`, leverageData),

    // 查询直播间合约下单
    getRoomOrders: (params: { roomId: number; page?: number; limit?: number; filterType?: string }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: HyperliquidRoomOrder[];
      }>(`/app/hyperliquid/room/orders`, params),

    // 查询直播间主播统计
    getRoomStats: (params: { roomId: number }) =>
      apiClient.get<{
        roomId: number;
        streamerId: number;
        today: {
          totalPnl: string;
          orderCount: number;
        };
        last7Days: {
          totalPnl: string;
          orderCount: number;
        };
        last14Days: {
          totalPnl: string;
          orderCount: number;
        };
        last30Days: {
          totalPnl: string;
          orderCount: number;
        };
        all: {
          totalPnl: string;
          orderCount: number;
        };
      }>(`/app/hyperliquid/room/pnl`, params),
  },

  // 直播间 Polymarket 预测订单与用户统计
  prediction: {
    getRoomOrders: (params: PolymarketRoomOrdersParams) =>
      apiClient.get<PolymarketRoomOrdersData>(`/app/prediction/room/orders`, params as Record<string, unknown>),
    getUserStats: (params?: PolymarketUserStatsParams) =>
      apiClient.get<PolymarketUserStatsData>(`/app/prediction/user/stats`, params as Record<string, unknown>),
  },

  // 通用接口
  common: {
    // 获取Banner列表
    getBannerList: () => apiClient.get<Announcement[]>(`/app/message/announcement/list`),
    // 获取官方公告列表
    getAnnouncementList: (params?: { page?: number; limit?: number }) =>
      apiClient.get<AnnouncementListResponse>(`/app/message/announcement/history`, params),
    // 获取公告详情
    getAnnouncementDetail: (params: { id: number }) => apiClient.get<Announcement>(`/app/message/announcement/detail`, params),
    // 搜索
    comprehensiveSearch: (params: { keyword: string }) =>
      apiClient.get<{
        liveRoomList: {
          userName: string;
          avatar: string;
          userId: number;
          roomId: number;
          roomName: string;
          handle?: string;
        }[];
        streamerList: {
          userName: string;
          avatar: string;
          userId: number;
          isStreamer: number;
          isRoom: number;
          roomId: number;
          userLabels?: string;
          handle?: string;
        }[];
        talkList: Post[];
      }>(`/app/Search/comprehensiveSearch`, params),

    // 直播间搜索
    searchLiveRooms: (params: { roomName: string; playingMethod: string; roomType: number | undefined; page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: LiveRoom[];
      }>(`/app/room/searchLiveRoomsWithIVS`, params),

    // 动态搜索
    searchTalks: (params: { keywords: string; page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: Post[];
      }>(`/app/gambit/searchTalks`, params),

    // 直播间搜索
    searchUserList: (params: { name: string; page: number; limit: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: UserProfile[];
      }>(`/app/user/searchUserList`, params),

    // 获取币接口
    getCryptoList: () => apiClient.get<CryptoData[]>(`/app/coinPrice/getMainCoins`),
    // 获取话题列表
    getHashList: (params: { page: number; size: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: HashData[];
      }>(`/app/gambit/getHotTags`, params),
    // 获取应用配置
    getAppConfig: () => apiClient.get(`/app/common/config`),

    // 获取内容分类列表
    getContentCategoryList: () => apiClient.get<ContentCategory[]>(`/app/contentCategory/list`),

    // 搜索接口
    search: (keyword: string, type?: string) => {
      const params = new URLSearchParams();
      params.append('keyword', keyword);
      if (type) params.append('type', type);
      return apiClient.get(`/app/common/search?${params.toString()}`);
    },
    // 计算签名
    generateUserSig: () => apiClient.get(`/app/room/generateUserSig`),
  },

  // Privy 相关接口
  privy: {
    // 获取服务端签名者 ID
    getSignerId: () => apiClient.get<{ signerId: string }>(`/app/dex/hyperliquid/signer-id`),
  },

  // 反馈/举报相关接口
  feedback: {
    // 获取举报类型列表
    getReportTypes: (params?: { lang?: string }) => apiClient.get<ReportType[]>(`/app/feedback/reportTypes`, params),

    // 获取举报分类列表
    getReportCategories: (params?: { lang?: string }) => apiClient.get<ReportCategory[]>(`/app/feedback/reportCategories`, params),

    // 举报直播间
    reportRoom: (data: { roomId: number; reportType: string; description?: string; screenshotUrl?: string }) =>
      apiClient.post(`/app/feedback/reportRoom`, data),

    // 举报用户
    reportUser: (data: { reportedUserId: number; reportType: string; reportCategory?: number; description?: string; screenshotUrl?: string }) =>
      apiClient.post(`/app/feedback/reportUser`, data),

    // 获取我的举报列表
    getMyReportList: (params: { page?: number; limit?: number; status?: number }) =>
      apiClient.get<{
        totalCount: number;
        pageSize: number;
        totalPage: number;
        currPage: number;
        list: ReportItem[];
      }>(`/app/feedback/myReportList`, params),

    // 获取用户举报列表（管理端接口）
    getUserReportList: (params: {
      page?: number;
      limit?: number;
      reportType?: string;
      status?: number;
      userName?: string;
      reportedUserId?: number;
    }) =>
      apiClient.get<{
        list: UserReportItem[];
      }>(`/app/feedback/myUserReportList`, params),
  },
};

export default api;
